import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'
import { ClientNciHistoryStatus } from '@model/CreateNciHistory'
import { NameDto } from '@model/NameDto'
import { UploadDocumentCategoryEnum } from '@model/UploadDocumentCategoryEnum'

import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'

import {
  DocumentManagementNCIForm,
  DocumentManagementNCIFormValues,
} from './DocumentManagementNCIForm/DocumentManagementNCIForm'

const useDocumentManagementNCIMutate = () => {
  const navigate = useNavigate()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const [isLoading, setLoading] = useState(false)
  const locale = useSessionLanguage()
  const { refreshAccount } = useAccountWriteContext()
  const { account } = useAccountReadContext()
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)
  const documentListKey = queryKeys.document.list({ locale }, locale)
  const generalStatusKey = queryKeys.document.generalStatus(account?.id, locale)

  const mutate = async (values: DocumentManagementNCIFormValues) => {
    try {
      setLoading(true)
      await apiClient.accountAcceptFinalNciHistory({
        statusId: ClientNciHistoryStatus.PENDING,
        nciPriorityId: values.accountNciAvailablePriorities.id,
        documentTypeId: values.nciHistory.documentTypeId,
        nciDocumentNumber: values.nciHistory.nciDocumentNumber,
        countryId: values.accountNciAvailablePriorities.country.id,
      })
      await apiClient.uploadDocuments({ documents: values.documents })
      await refreshAccount(locale)

      queryClient.removeQueries({
        queryKey: documentListKey,
        exact: false,
      })

      queryClient.removeQueries({
        queryKey: generalStatusKey,
        exact: false,
      })

      navigate(CommonPaths.DocumentManagement)
    } catch (error: unknown) {
      alreadyUploadedModal.open({ message: (error as any).response?.response?.data?.code })
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  return {
    mutate,
    isLoading,
  }
}

export const DocumentManagementNCIPage: React.FC = () => {
  const navigate = useNavigate()

  const location = useLocation()

  const [values] = useState<{
    documentCategory: NameDto
    documentCategoryType: NameDto
  }>(location.state)
  const [accountNciAvailablePriorities, setAccountNciAvailablePriorities] =
    useState<ClientNciPriorityDto>()
  const apiClient = useApiClient(ClientApiClient)

  const locale = useSessionLanguage()

  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.profile.nciData(locale),
    queryFn: () =>
      Promise.all([
        apiClient.getAccountNciPriorities(),
        apiClient.getAccountNciAvailablePriorities(locale),
      ]),
  })
  const [accountNciPriorities = [], accountNciAvailablePrioritiesFetch] = data

  const mutation = useDocumentManagementNCIMutate()

  const handleSubmit = async (values: DocumentManagementNCIFormValues) => {
    await mutation.mutate(values)
  }

  const handlePageExitConfirmation = () => {
    navigate(CommonPaths.DocumentManagement)
  }
  const { t } = useTranslation()
  useEffect(() => {
    setAccountNciAvailablePriorities(accountNciAvailablePrioritiesFetch)
  }, [accountNciAvailablePrioritiesFetch])

  const handleChooseAnotherDoc = (doc: ClientNciPriorityDto) => {
    setAccountNciAvailablePriorities(doc)
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      {!mutation.isLoading && (
        <React.Fragment>
          {accountNciAvailablePriorities && (
            <DocumentManagementNCIForm
              documentCategory={
                values?.documentCategory || {
                  id: UploadDocumentCategoryEnum.Personal,
                }
              }
              handleChooseAnotherDoc={handleChooseAnotherDoc}
              documentCategoryType={values?.documentCategoryType}
              accountNciPriorities={accountNciPriorities}
              accountNciAvailablePriorities={accountNciAvailablePriorities}
              onSubmit={handleSubmit}
              onCancel={handlePageExitConfirmation}
            />
          )}
        </React.Fragment>
      )}
      {mutation.isLoading && (
        <Loading isLoading={mutation.isLoading} showLoadingIcon text={t('Wallet.Submitting')} />
      )}
    </Loading>
  )
}
